/**
 *
 * @module bamIdentityClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/identity-bam.md
 * @see https://github.bamtech.co/services-commons/public-api/blob/master/swagger/services/bam-idp.md
 *
 */

/* eslint-disable camelcase */

import Check from 'check-types';
import Logging from './../../../logging/entry';
import Exception from './../../exception/entry';
import HttpClient from './../../platforms/httpClient';
import Token from './../../token/entry';
import Util from './../../util/entry';
import DustLogUtility from './../../internal/dust/dustLogUtility';
import DustUrnReference from './../../internal/dust/dustUrnReference';
import Configuration from './../../configuration/entry';

import AuthenticationFlowResponse from './authenticationFlowResponse';
import BamIdentityClientConfiguration from './bamIdentityClientConfiguration';
import BamIdentityClientEndpoint from './bamIdentityClientEndpoint';
import RedeemedPasscodeToken from './redeemedPasscodeToken';
import OneTimePasscodeRequestReason from './oneTimePasscodeRequestReason';
import Identity from './identity';

const AccessToken = Token.AccessToken;
const IdentityToken = Token.IdentityToken;
const HttpMethod = Configuration.HttpMethod;
const BamIdentityClientDustUrnReference = DustUrnReference.services.identity.bam.bamIdentityClient;

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const {
    nonEmptyString,
    object,
    instanceStrict,
    includes
} = Check.assert;

/**
 *
 * @class BamIdentityClient
 * @implements {IBamIdentityClient}
 * @desc Provides a data client that can be used to access BAM identity services.
 *
 */
export default class BamIdentityClient {

    /**
     *
     * @param {SDK.Services.BamIdentityClient} bamIdentityClient
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     *
     */
    constructor(bamIdentityClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'BamIdentityClient(bamIdentityClientConfiguration, logger, httpClient)',
            get bamIdentityClientConfiguration() {
                return `${this.ctor} needs BamIdentityClientConfiguration instance`;
            }
        };

        instanceStrict(
            bamIdentityClientConfiguration,
            BamIdentityClientConfiguration,
            errorMessage.bamIdentityClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {BamIdentityClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = bamIdentityClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(BamIdentityClient.name, 'Created');

    }

    /**
     *
     * @access public
     * @param {String} email - The email associated with the identity
     * @param {String} password - The password associated with the identity.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Exchange proof of identity (credentials) for a BAM id token.
     * @returns {Promise<SDK.Services.Token.IdentityToken>} The identity token if successfully created.
     *
     */
    authenticate(email, password, accessToken) {

        const errorMessage = {
            method: 'BamIdentityClient.authenticate(email, password, accessToken)',
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };


        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken.token,
            BamIdentityClientEndpoint.identityLogin,
            {
                email,
                password
            }
        );

        logger.info(BamIdentityClient.name, `Attempting to authenticate, email associated with identity: "${email}"`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.authenticate,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { id_token, expires_in } = data;

            const expiration = new Date(Date.now() + (expires_in * 1000)).getTime();

            return Promise.resolve(
                new IdentityToken(id_token, expiration)
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} email - The email for the new identity.
     * @param {String} password - The password for the new identity.
     * @param {Object} [attributes]
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @note {Object} [metadata] - Contains additional information about the identity being created (ex: isTest)
     * @desc Register a new identity by providing registration details.
     * @returns {Promise<Void>} An awaitable task indicating settings were updated successfully.
     *
     */
    createIdentity(email, password, attributes, accessToken, metadata) {

        const errorMessage = {
            method: 'BamIdentityClient.createIdentity(email, password, attributes, accessToken, metadata)',
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            },
            get attributes() {
                return `${this.method} attributes should be an Object`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            },
            get metadata() {
                return `${this.method} metadata should be an Object`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);
        object(attributes, errorMessage.attributes, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        if (Check.assigned(metadata)) {
            object(metadata, errorMessage.metadata, InvalidArgumentException);
        }

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken.token,
            BamIdentityClientEndpoint.createIdentity,
            {
                email,
                password,
                attributes,
                metadata
            },
        );

        logger.info(BamIdentityClient.name, `Attempting to register new identity, email: "${email}"`);

        if (Check.nonEmptyObject(attributes)) {
            logger.info(BamIdentityClient.name, `Identity attributes to create - "${JSON.stringify(attributes)}".`);
        }

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.createIdentity,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} email - The new email for associated with the identity.
     * @param {String} password - The current password associated with the
     * identity used to verify the user's credentials.
     * @param {SDK.Services.Token.IdentityToken} identityToken - The identity token to provide user context.
     * @desc Changes the email of the credentials for a given BAM identity.
     * @returns {Promise<Void>}
     *
     */
    changeEmail(email, password, identityToken) {

        const errorMessage = {
            method: 'BamIdentityClient.changeEmail(email, password, identityToken)',
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get password() {
                return `${this.method} password should be a String with length`;
            },
            get identityToken() {
                return `${this.method} needs SDK.Services.Token.IdentityToken instance`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(password, errorMessage.password, InvalidArgumentException);
        instanceStrict(identityToken, IdentityToken, errorMessage.identityToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            identityToken.token,
            BamIdentityClientEndpoint.changeEmail,
            {
                email,
                password
            }
        );

        logger.info(BamIdentityClient.name, `Attempting to update email assocaited with identity, new email: "${email}"`);// eslint-disable-line max-len

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.changeEmail,
            payload,
            method: HttpMethod.PATCH
        });

        return httpClient.patch(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'changeEmail');

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} newPassword - The new password to associate with the identity.
     * @param {String} [oldPassword] - The current password associated with the
     * identity used to verify the user's credentials.
     * @param {SDK.Services.Token.IdentityToken} identityToken - The identity token to provide user context.
     * @desc Change the password for a given BAM identity.
     * @returns {Promise<Void>} An awaitable task indicating settings were updated successfully.
     *
     */
    changePassword(newPassword, oldPassword, identityToken) {

        const errorMessage = {
            method: 'BamIdentityClient.changePassword(newPassword, oldPassword, identityToken)',
            get newPassword() {
                return `${this.method} newPassword should be a String with length`;
            },
            get oldPassword() {
                return `${this.method} oldPassword should be a String with length`;
            },
            get identityToken() {
                return `${this.method} needs SDK.Services.Token.IdentityToken instance`;
            }
        };

        nonEmptyString(newPassword, errorMessage.newPassword, InvalidArgumentException);

        if (Check.assigned(oldPassword)) {
            nonEmptyString(oldPassword, errorMessage.oldPassword, InvalidArgumentException);
        }

        instanceStrict(identityToken, IdentityToken, errorMessage.identityToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            identityToken.token,
            BamIdentityClientEndpoint.changePassword,
            {
                newPassword,
                oldPassword
            }
        );

        logger.info(BamIdentityClient.name, 'Attempting to update password assocaited with identity');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.changePassword,
            payload,
            method: HttpMethod.PATCH
        });

        return httpClient.patch(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} email - The email address to send an email to
     * containing instructions to start the password reset.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context
     * @desc Requests a password reset.
     * @returns {Promise<Void>} An awaitable task indicating settings were updated successfully.
     *
     */
    requestPasswordReset(email, accessToken) {

        const errorMessage = {
            method: 'BamIdentityClient.requestPasswordReset(email, accessToken)',
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken.token,
            BamIdentityClientEndpoint.startPasswordReset,
            {
                email
            }
        );

        logger.info(BamIdentityClient.name, `Request password reset, sending instructions to email: "${email}"`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.requestPasswordReset,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {String} newPassword - The new password to associate with the identity.
     * @param {String} resetToken - Details needed to reset the password of an identity.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Reset the password for a given MLBAM identity.
     * @returns {Promise<Void>} An awaitable task indicating settings were updated successfully.
     *
     */
    resetPassword(newPassword, passwordResetToken, accessToken) {

        const errorMessage = {
            method: 'BamIdentityClient.resetPassword(newPassword, resetToken, accessToken)',
            get newPassword() {
                return `${this.method} newPassword should be a String with length`;
            },
            get passwordResetToken() {
                return `${this.method} passwordResetToken should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(newPassword, errorMessage.newPassword, InvalidArgumentException);
        nonEmptyString(passwordResetToken, errorMessage.passwordResetToken, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken.token,
            BamIdentityClientEndpoint.resetPassword,
            {
                newPassword,
                passwordResetToken
            }
        );

        logger.info(BamIdentityClient.name, 'Attempting to reset password for the given identity.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.resetPassword,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {SDK.Services.Token.IdentityToken} identityToken - The identity token to provide user context.
     * @desc Returns an Identity
     * @returns {Promise<Identity>}
     *
     */
    getIdentity(identityToken) {

        const errorMessage = {
            method: 'BamIdentityClient.getIdentity(identityToken)',
            get identityToken() {
                return `${this.method} needs SDK.Services.Token.IdentityToken instance`;
            }
        };

        instanceStrict(identityToken, IdentityToken, errorMessage.identityToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            identityToken.token,
            BamIdentityClientEndpoint.getIdentity
        );

        logger.info(BamIdentityClient.name, 'Attempting to retrieve identity.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.getIdentity,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { attributes, email, id, partner } = data;

            return Promise.resolve(new Identity(email, id, partner, attributes));

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @param {Object} attributes - The identity attributes to update.
     * @param {SDK.Services.Token.IdentityToken} identityToken - The identity token to provide user context.
     * @desc Update the identity attributes of the identity identified by the identity token,
     * if field(s) are missing from the request body, the original value will be left intact.
     * @returns {Promise<Void>}
     *
     */
    updateAttributes(attributes, identityToken) {

        const errorMessage = {
            method: 'BamIdentityClient.updateAttributes(attributes, identityToken)',
            get attributes() {
                return `${this.method} attributes should be an Object`;
            },
            get identityToken() {
                return `${this.method} needs SDK.Services.Token.IdentityToken instance`;
            }
        };

        object(attributes, errorMessage.attributes, InvalidArgumentException);
        instanceStrict(identityToken, IdentityToken, errorMessage.identityToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            identityToken.token,
            BamIdentityClientEndpoint.updateAttributes,
            attributes
        );

        logger.info(BamIdentityClient.name, 'Update identity attributes.');

        if (Check.nonEmptyObject(attributes)) {
            logger.info(BamIdentityClient.name, `Identity attributes to update - "${JSON.stringify(attributes)}".`);
        }

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.updateAttributes,
            payload,
            method: HttpMethod.PATCH
        });

        return httpClient.patch(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @param {String} email
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @desc checks if the email address that is provided is registered with the BamIdp service
     * @returns {SDK.Services.Identity.Bam.AuthenticationFlowResponse}
     *
     */
    check(email, accessToken) {

        const errorMessage = {
            method: `${this.constructor.name}.check(email, accessToken)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken.token,
            BamIdentityClientEndpoint.check,
            {
                email
            }
        );

        logger.info(BamIdentityClient.name, 'Validating email against BamIdp service.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.check,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((ex) => {

            throw new ServiceException('', [], ex);

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility, 'check');

        }).then((response) => {

            const { data } = response;
            const { operations } = data;

            const authenticationFlowResponse = new AuthenticationFlowResponse(operations);

            return Promise.resolve(authenticationFlowResponse);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @param {String} email - The email to send the passcode to.
     * @param {SDK.Services.Identity.Bam.OneTimePasscodeRequestReason} reason - The intended use of the passcode.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context
     * @desc Request that a one-time passcode be sent to an email address.
     * @returns {Promise<Void>}
     *
     */
    requestOneTimePasscode(email, reason, accessToken) {

        const errorMessage = {
            method: `${this.constructor.name}.requestOneTimePasscode(email, reason, accessToken)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get reason() {
                return `${this.method} reason should be a valid SDK.Services.Identity.Bam.OneTimePasscodeRequestReason`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        includes(OneTimePasscodeRequestReason, reason, errorMessage.reason, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken.token,
            BamIdentityClientEndpoint.otpRequest,
            {
                email,
                reason
            }
        );

        logger.info(BamIdentityClient.name, 'Request One Time Passcode');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.requestOneTimePasscode,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((ex) => {

            throw new ServiceException('', [], ex);

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access public
     * @since 3.9.0
     * @param {String} email - The email that received the passcode.
     * @param {String} passcode - The passcode sent to the email associated with the identity.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context
     * @desc Logs the user in with the BAM identity service.
     * @returns {Promise<SDK.Services.Token.IdentityToken>}
     *
     */
    redeemOneTimePasscode(email, passcode, accessToken) {

        const errorMessage = {
            method: `${this.constructor.name}.redeemOneTimePasscode(email, passcode, accessToken)`,
            get email() {
                return `${this.method} email should be a String with length`;
            },
            get passcode() {
                return `${this.method} passcode should be a String with length`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(email, errorMessage.email, InvalidArgumentException);
        nonEmptyString(passcode, errorMessage.passcode, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken.token,
            BamIdentityClientEndpoint.otpRedeem,
            {
                email,
                passcode
            }
        );

        logger.info(BamIdentityClient.name, 'Redeem One Time Passcode');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: BamIdentityClient.name,
            urn: BamIdentityClientDustUrnReference.redeemOneTimePasscode,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((ex) => {

            throw new ServiceException('', [], ex);

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;
            const { id_token, expires_in, reason } = data;

            const expiration = new Date(Date.now() + (expires_in * 1000)).getTime();

            return Promise.resolve(
                new RedeemedPasscodeToken(id_token, expiration, reason)
            );

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {String} token - The AccessToken or IdentityToken token String value
     * @param {String} rel
     * @param {Object} body
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(token, rel, body) {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestBody = body ? JSON.stringify(body) : '';
        const requestHeaders = Object.assign({}, headers);

        if (requestHeaders.Authorization.match(/\{identityToken\}/gi)) {
            requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{identityToken\}/gi, token);
        } else {
            requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, token);
        }

        return {
            url: href,
            body: requestBody,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/identity/bam/bamIdentityClient.js